import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Check, Clock, Ticket, X, ChevronRight, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { parseApiDate } from '../lib/utils';
import AttachmentsPanel from '../components/shared/Attachments';

// ── Status meta ───────────────────────────────────────────────
const STATUS_META = {
  planning:    { label: 'Planning',    color: '#6366f1' },
  in_progress: { label: 'In Progress', color: '#10b981' },
  on_hold:     { label: 'On Hold',     color: '#f59e0b' },
  completed:   { label: 'Completed',   color: '#64748b' },
  cancelled:   { label: 'Cancelled',   color: '#ef4444' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#888' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: m.color + '1a', color: m.color, border: `1px solid ${m.color}33` }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

// ── Overview tab ──────────────────────────────────────────────
function OverviewTab({ project, members, orgs, techs, isStaff, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const startEdit = () => {
    setForm({
      title: project.title, description: project.description || '',
      status: project.status, org_id: project.org?.id || '',
      lead_tech_id: project.lead_tech?.id || '',
      estimated_hours: project.estimated_hours || '',
      budget: project.budget || '',
      started_at: project.started_at?.slice(0,10) || '',
      due_date: project.due_date?.slice(0,10) || '',
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`/projects/${project.id}`, {
        ...form,
        estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
        budget: form.budget ? parseFloat(form.budget) : null,
        org_id: form.org_id || null,
        lead_tech_id: form.lead_tech_id || null,
        started_at: form.started_at || null,
        due_date: form.due_date || null,
      });
      onUpdate(updated.project);
      setEditing(false);
      toast.success('Project updated');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hours = project.total_minutes > 0 ? (project.total_minutes / 60).toFixed(1) : null;
  const pct   = project.milestone_count > 0 ? Math.round((project.milestones_done / project.milestone_count) * 100) : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
      {/* Main */}
      <div className="card card-lg">
        <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14 }}>Details</h3>
          {isStaff && !editing && <button className="btn btn-ghost btn-sm" onClick={startEdit}><Edit size={14} /> Edit</button>}
          {isStaff && editing && (
            <div className="flex gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            </div>
          )}
        </div>

        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Description</label>
              <textarea className="form-textarea" style={{ minHeight: 100 }} value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(STATUS_META).map(([v,m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Organization</label>
                <select className="form-select" value={form.org_id} onChange={e => set('org_id', e.target.value)}>
                  <option value="">None</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Lead Tech</label>
                <select className="form-select" value={form.lead_tech_id} onChange={e => set('lead_tech_id', e.target.value)}>
                  <option value="">None</option>
                  {techs.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Estimated Hours</label>
                <input className="form-input" type="number" min="0" step="0.5" value={form.estimated_hours} onChange={e => set('estimated_hours', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Budget ($)</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.budget} onChange={e => set('budget', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Start Date</label>
                <input className="form-input" type="date" value={form.started_at} onChange={e => set('started_at', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Due Date</label>
                <input className="form-input" type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
              </div>
            </div>
          </div>
        ) : (
          <>
            {project.description
              ? <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>{project.description}</p>
              : <p style={{ fontSize: 14, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 16 }}>No description.</p>
            }
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                ['Organization',    project.org?.name        || '—'],
                ['Lead Tech',       project.lead_tech?.full_name || '—'],
                ['Estimated Hours', project.estimated_hours ? `${project.estimated_hours}h` : '—'],
                ['Budget',          project.budget ? `$${Number(project.budget).toLocaleString()}` : '—'],
                ['Start Date',      project.started_at ? parseApiDate(project.started_at).toLocaleDateString() : '—'],
                ['Due Date',        project.due_date   ? parseApiDate(project.due_date).toLocaleDateString()   : '—'],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{val}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Sidebar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Stats */}
        <div className="card card-lg">
          <h3 style={{ fontSize: 13, marginBottom: 14 }}>Stats</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {hours && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Time logged</span>
                  <span style={{ fontWeight: 600 }}>{hours}h {project.estimated_hours ? `/ ${project.estimated_hours}h` : ''}</span>
                </div>
                {project.estimated_hours && (
                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 99 }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (parseFloat(hours) / project.estimated_hours) * 100)}%`, background: 'var(--accent)', borderRadius: 99 }} />
                  </div>
                )}
              </div>
            )}
            {pct !== null && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Milestones</span>
                  <span style={{ fontWeight: 600 }}>{project.milestones_done} / {project.milestone_count}</span>
                </div>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 99 }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: '#10b981', borderRadius: 99 }} />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>Linked tickets</span>
              <span style={{ fontWeight: 600 }}>{project.ticket_count}</span>
            </div>
          </div>
        </div>

        {/* Members */}
        <div className="card card-lg">
          <h3 style={{ fontSize: 13, marginBottom: 12 }}>Team</h3>
          {members.length === 0
            ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No members yet.</p>
            : members.map(m => (
              <div key={m.id} className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                <div className="avatar" style={{ width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
                  {(m.full_name || m.email || '?').split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{m.full_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{m.role}</div>
                </div>
              </div>
            ))
          }
        </div>

        {/* Attachments */}
        {project?.id && <AttachmentsPanel parentType="project" parentId={project.id} />}
      </div>
    </div>
  );
}

// ── Milestones tab ────────────────────────────────────────────
function MilestonesTab({ projectId, isStaff, onMilestoneChange }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [adding, setAdding]         = useState(false);
  const [newTitle, setNewTitle]     = useState('');
  const [newDue, setNewDue]         = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/projects/${projectId}/milestones`);
      setMilestones(d.milestones || []);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const addMilestone = async () => {
    if (!newTitle.trim()) return;
    try {
      const d = await api.post(`/projects/${projectId}/milestones`, { title: newTitle, due_date: newDue || null });
      setMilestones(ms => [...ms, d.milestone]);
      setNewTitle(''); setNewDue(''); setAdding(false);
      onMilestoneChange?.();
    } catch (err) { toast.error(err.message || 'Failed to add milestone'); }
  };

  const toggle = async (m) => {
    try {
      const d = await api.put(`/projects/${projectId}/milestones/${m.id}`, { completed: !m.completed_at });
      setMilestones(ms => ms.map(x => x.id === m.id ? d.milestone : x));
      onMilestoneChange?.();
    } catch (err) { toast.error(err.message || 'Failed to update'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      await api.delete(`/projects/${projectId}/milestones/${id}`);
      setMilestones(ms => ms.filter(m => m.id !== id));
      onMilestoneChange?.();
    } catch (err) { toast.error(err.message || 'Failed to delete'); }
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 680 }}>
      <div className="card card-lg">
        <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14 }}>Milestones</h3>
          {isStaff && <button className="btn btn-secondary btn-sm" onClick={() => setAdding(a => !a)}><Plus size={14} /> Add Milestone</button>}
        </div>

        {adding && (
          <div style={{ padding: 14, background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input className="form-input" placeholder="Milestone title…" value={newTitle} onChange={e => setNewTitle(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && addMilestone()} />
            <div className="flex gap-2 items-center">
              <input className="form-input" type="date" value={newDue} onChange={e => setNewDue(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-primary btn-sm" onClick={addMilestone}>Add</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        )}

        {milestones.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No milestones yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {milestones.map((m, i) => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: m.completed_at ? 'var(--bg-secondary)' : 'transparent', opacity: m.completed_at ? 0.65 : 1 }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12, width: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                {isStaff ? (
                  <button
                    onClick={() => toggle(m)}
                    style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${m.completed_at ? '#10b981' : 'var(--border)'}`, background: m.completed_at ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
                  >
                    {m.completed_at && <Check size={12} color="white" />}
                  </button>
                ) : (
                  <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${m.completed_at ? '#10b981' : 'var(--border)'}`, background: m.completed_at ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {m.completed_at && <Check size={12} color="white" />}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, textDecoration: m.completed_at ? 'line-through' : 'none' }}>{m.title}</div>
                  {m.due_date && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Due {parseApiDate(m.due_date).toLocaleDateString()}</div>}
                </div>
                {isStaff && (
                  <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px', color: 'var(--text-muted)' }} onClick={() => remove(m.id)}><X size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Time tab ──────────────────────────────────────────────────
function TimeTab({ projectId, isStaff }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState('');
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/projects/${projectId}/time`);
      setEntries(d.entries || []);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const totalMins = entries.reduce((s, e) => s + e.minutes, 0);

  const logTime = async () => {
    if (!minutes || parseInt(minutes) <= 0) return toast.error('Enter minutes');
    setSaving(true);
    try {
      const d = await api.post(`/projects/${projectId}/time`, { minutes: parseInt(minutes), notes });
      setEntries(es => [d.entry, ...es]);
      setMinutes(''); setNotes('');
      toast.success('Time logged');
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/time/${id}`);
      setEntries(es => es.filter(e => e.id !== id));
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 680 }}>
      {isStaff && (
        <div className="card card-lg" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, marginBottom: 14 }}>Log Time</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ marginBottom: 0, width: 120 }}>
              <label className="form-label">Minutes</label>
              <input className="form-input" type="number" min="1" placeholder="e.g. 60" value={minutes} onChange={e => setMinutes(e.target.value)} />
            </div>
            <div className="form-group flex-1" style={{ marginBottom: 0 }}>
              <label className="form-label">Notes</label>
              <input className="form-input" placeholder="What did you work on?" value={notes} onChange={e => setNotes(e.target.value)} onKeyDown={e => e.key === 'Enter' && logTime()} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 0 }}>
              <button className="btn btn-primary" onClick={logTime} disabled={saving}><Clock size={14} /> Log</button>
            </div>
          </div>
        </div>
      )}

      <div className="card card-lg">
        <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14 }}>Time Entries</h3>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Total: <strong>{(totalMins / 60).toFixed(1)}h</strong></span>
        </div>
        {entries.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No time logged yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {entries.map(e => (
              <div key={e.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 10px', borderRadius: 8, background: 'var(--bg-secondary)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{(e.minutes / 60).toFixed(1)}h</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13 }}>{e.notes || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No notes</span>}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.tech_name} · {formatDistanceToNow(parseApiDate(e.logged_at), { addSuffix: true })}</div>
                </div>
                {isStaff && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--text-muted)' }} onClick={() => remove(e.id)}><X size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tickets tab ───────────────────────────────────────────────
function TicketsTab({ projectId, isStaff }) {
  const navigate = useNavigate();
  const [tickets,    setTickets]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [searching,  setSearching]  = useState(false);
  const [results,    setResults]    = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/projects/${projectId}/tickets`);
      setTickets(d.tickets || []);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim() || search.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const d = await api.get(`/tickets?search=${encodeURIComponent(search)}&limit=10`);
        const linkedIds = new Set(tickets.map(t => t.id));
        setResults((d.tickets || []).filter(t => !linkedIds.has(t.id)));
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, tickets]);

  const link = async (ticket) => {
    try {
      await api.post(`/projects/${projectId}/tickets`, { ticket_id: ticket.id });
      setTickets(ts => [{ ...ticket, status_name: ticket.status?.name, status_color: ticket.status?.color, priority_name: ticket.priority?.name, creator_name: ticket.created_by?.full_name }, ...ts]);
      setSearch(''); setResults([]); setShowSearch(false);
      toast.success(`Ticket #${ticket.ticket_number} linked`);
    } catch (err) { toast.error(err.message || 'Failed to link'); }
  };

  const unlink = async (ticketId) => {
    try {
      await api.delete(`/projects/${projectId}/tickets/${ticketId}`);
      setTickets(ts => ts.filter(t => t.id !== ticketId));
    } catch (err) { toast.error(err.message || 'Failed to unlink'); }
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="card card-lg">
        <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14 }}>Linked Tickets</h3>
          {isStaff && <button className="btn btn-secondary btn-sm" onClick={() => setShowSearch(s => !s)}><Plus size={14} /> Link Ticket</button>}
        </div>

        {showSearch && (
          <div style={{ marginBottom: 16, padding: 14, background: 'var(--bg-secondary)', borderRadius: 8 }}>
            <input className="form-input" placeholder="Search by ticket number or subject…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
            {searching && <div style={{ padding: '8px 0', fontSize: 13, color: 'var(--text-muted)' }}>Searching…</div>}
            {results.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {results.map(t => (
                  <div key={t.id} onClick={() => link(t)} style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--bg)', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg)'}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>#{t.ticket_number}</span>
                    <span style={{ fontSize: 13, flex: 1 }}>{t.subject}</span>
                    <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tickets.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tickets linked yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {tickets.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-secondary)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>#{t.ticket_number}</span>
                <span style={{ fontSize: 13, flex: 1, cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate(`/tickets/${t.id}`)}>{t.subject}</span>
                {t.status_name && (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: (t.status_color || '#888') + '1a', color: t.status_color || '#888', whiteSpace: 'nowrap' }}>
                    {t.status_name}
                  </span>
                )}
                {isStaff && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--text-muted)', flexShrink: 0 }} onClick={() => unlink(t.id)}><X size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main detail page ──────────────────────────────────────────
const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'milestones', label: 'Milestones' },
  { id: 'time',       label: 'Time' },
  { id: 'tickets',    label: 'Tickets' },
];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [project,  setProject]  = useState(null);
  const [members,  setMembers]  = useState([]);
  const [orgs,     setOrgs]     = useState([]);
  const [techs,    setTechs]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('overview');

  const load = useCallback(async () => {
    try {
      const [d, od, td] = await Promise.all([
        api.get(`/projects/${id}`),
        isStaff ? api.get('/orgs') : Promise.resolve({ orgs: [] }),
        isStaff ? api.get('/users') : Promise.resolve({ users: [] }),
      ]);
      setProject(d.project);
      setMembers(d.members || []);
      setOrgs(od.orgs || []);
      setTechs((td.users || []).filter(u => ['admin','tech'].includes(u.role)));
    } catch (err) {
      toast.error('Project not found');
      navigate('/projects');
    } finally { setLoading(false); }
  }, [id, isStaff]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (err) { toast.error(err.message || 'Failed to delete'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><div className="spinner" /></div>;
  if (!project) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ marginBottom: 0 }}>{project.title}</h2>
            <StatusBadge status={project.status} />
          </div>
          {project.org && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{project.org.name}</div>}
        </div>
        {isStaff && (
          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red, #ef4444)' }} onClick={handleDelete}>
            <Trash2 size={14} /> Delete
          </button>
        )}
      </div>

      <div className="page-body">
        {/* Tabs */}
        <div className="flex gap-1 mb-5" style={{ borderBottom: '1px solid var(--border)' }}>
          {TABS.map(t => (
            <button key={t.id} className="btn btn-ghost" onClick={() => setTab(t.id)} style={{
              borderRadius: '6px 6px 0 0',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
              fontWeight: tab === t.id ? 600 : 400, paddingBottom: 10,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview'   && <OverviewTab project={project} members={members} orgs={orgs} techs={techs} isStaff={isStaff} onUpdate={p => setProject(p)} />}
        {tab === 'milestones' && <MilestonesTab projectId={id} isStaff={isStaff} onMilestoneChange={load} />}
        {tab === 'time'       && <TimeTab projectId={id} isStaff={isStaff} />}
        {tab === 'tickets'    && <TicketsTab projectId={id} isStaff={isStaff} />}
      </div>
    </div>
  );
}
